import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { PageHero } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, ArrowLeft, CalendarClock } from "lucide-react";
import { useSelectedEvent } from "@/lib/event-context";
import {
  type EventItem,
  resolveEventImageUrl,
  getEventStatusRange,
  getPastEvents,
  getEventYear,
  extractYears,
  formatEventDateRange,
} from "@/lib/events";

export const Route = createFileRoute("/past-events")({
  component: PastEventsPage,
  head: () => ({
    meta: [
      { title: "Past Events — UASA Events Program" },
      {
        name: "description",
        content:
          "Browse the archive of past UASA conferences and gatherings across the Arab capital markets.",
      },
    ],
  }),
});

function PastEventsPage() {
  const { events, selectEvent } = useSelectedEvent();
  const [activeYear, setActiveYear] = useState<string>("all");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // All past events, most recent first (getPastEvents already sorts desc by date)
  const allPastEvents = useMemo(() => getPastEvents(events), [events]);
  const years = useMemo(() => extractYears(events), [events]);

  const visibleEvents = useMemo(() => {
    if (activeYear === "all") return allPastEvents;
    return allPastEvents.filter(
      (event) => getEventYear(event.event_date_from ?? event.event_date) === activeYear,
    );
  }, [allPastEvents, activeYear]);

  return (
    <>
      <PageHero
        eyebrow="Archive"
        title="Past Events"
        subtitle="A look back at previous UASA conferences and gatherings."
      />

      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background via-secondary/30 to-background">
        <div className="max-w-7xl mx-auto">
          {/* Back link */}
          <Button asChild variant="ghost" className="mb-6 -ml-2 text-muted-foreground">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Link>
          </Button>

          {allPastEvents.length === 0 ? (
            <Card className="p-12 text-center border-dashed">
              <CalendarClock className="w-10 h-10 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">There are no past events to show yet.</p>
            </Card>
          ) : (
            <>
              {/* Year filter */}
              {years.length > 1 && (
                <div
                  className={`flex flex-wrap gap-2 mb-8 sm:mb-10 ${mounted ? "animate-fade-in-up" : "opacity-0"}`}
                >
                  <button
                    onClick={() => setActiveYear("all")}
                    className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium uppercase tracking-wide transition-colors ${
                      activeYear === "all"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-muted-foreground hover:text-primary hover:bg-primary/10"
                    }`}
                  >
                    All
                  </button>
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => setActiveYear(year)}
                      className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium uppercase tracking-wide transition-colors ${
                        activeYear === year
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-muted-foreground hover:text-primary hover:bg-primary/10"
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}

              {visibleEvents.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                  <p className="text-muted-foreground">No past events in {activeYear}.</p>
                </Card>
              ) : (
                <div
                  className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 ${mounted ? "animate-fade-in-up" : "opacity-0"}`}
                >
                  {visibleEvents.map((event) => {
                    const imageUrl = resolveEventImageUrl(event.image_url);
                    return (
                      <Card
                        key={event.id}
                        className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border/50 flex flex-col"
                      >
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-3 left-3 flex gap-2">
                            <Badge className="bg-black/50 text-white border-0">Past</Badge>
                            {event.category && (
                              <Badge variant="secondary" className="bg-white/90 text-foreground">
                                {event.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="p-4 sm:p-5 flex flex-col flex-1">
                          <h4 className="font-serif text-lg text-primary leading-snug mb-2 line-clamp-2">
                            {event.title}
                          </h4>
                          {(event.event_date_from || event.event_date) && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                              <Calendar className="w-3 h-3" /> {formatEventDateRange(event)}
                            </p>
                          )}
                          {event.location && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
                              <MapPin className="w-3 h-3" /> {event.location}
                            </p>
                          )}
                          <div className="mt-auto">
                            <Button asChild size="sm" variant="outline" className="w-full">
                              <Link
                                to="/events/$eventId"
                                params={{ eventId: String(event.id) }}
                                onClick={() => selectEvent(event.id)}
                              >
                                View event
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
